import type {
  DataDisplay,
  ExpandableTable,
  ExpandableTableRowData,
  ChartData,
  QuoteList,
  Order,
  Report,
  Row,
  Rows,
  Shipment,
  SubTable,
  ProductWeightSectionData,
  PackhouseProducts,
  PackhouseProductsMixed,
  MarketTypeResults,
  CatalogMarketType,
  MarketGroupResults,
  CatalogMarketGroup,
} from 'generated/graphql';

/**
 * Type guard that narrows a GraphQL union member by its __typename.
 * Works with any union type whose members have a `__typename` field.
 *
 * @example
 *   if (isTypename(row.data, 'Rows')) {
 *     row.data.rows // narrowed to Rows
 *   }
 */
export function isTypename<T extends { __typename?: string }, N extends string>(
  value: T | null | undefined,
  typename: N,
): value is Extract<T, { __typename?: N }> {
  return value?.__typename === typename;
}

// --- DataDisplay guards ---

export function isExpandableTable(d: DataDisplay | null | undefined): d is ExpandableTable {
  return isTypename(d, 'ExpandableTable');
}

export function isChart(d: DataDisplay | null | undefined): d is ChartData {
  return isTypename(d, 'ChartData');
}

export function isQuoteList(d: DataDisplay | null | undefined): d is QuoteList {
  return isTypename(d, 'QuoteList');
}

// --- ExpandableTableRowData guards ---

export function isOrder(d: ExpandableTableRowData | null | undefined): d is Order {
  return isTypename(d, 'Order');
}

export function isReport(d: ExpandableTableRowData | null | undefined): d is Report {
  return isTypename(d, 'Report');
}

export function isRows(d: ExpandableTableRowData | null | undefined): d is Rows {
  return isTypename(d, 'Rows');
}

/**
 * Type guard for Rows data accessed via the `itemRows: rows` GraphQL alias
 * in Fragments.gql. The alias exists to avoid a naming collision with SubTable.rows
 * in the same fragment spread.
 */
export type ItemRows = Omit<Rows, 'rows'> & { itemRows: Row[] };
export function isItemRows(d: { __typename?: string } | null | undefined): d is ItemRows {
  return d?.__typename === 'Rows';
}

export function isShipment(d: ExpandableTableRowData | null | undefined): d is Shipment {
  return isTypename(d, 'Shipment');
}

export function isSubTable(d: ExpandableTableRowData | null | undefined): d is SubTable {
  return isTypename(d, 'SubTable');
}

// --- ProductWeightSectionData guards ---

export function isPackhouseProducts(d: ProductWeightSectionData | null | undefined): d is PackhouseProducts {
  return isTypename(d, 'PackhouseProducts');
}

export function isPackhouseProductsMixed(d: ProductWeightSectionData | null | undefined): d is PackhouseProductsMixed {
  return isTypename(d, 'PackhouseProductsMixed');
}

// --- Market type guards ---

export function isMarketTypeResults(d: MarketTypeResults | CatalogMarketType): d is MarketTypeResults {
  return isTypename(d, 'MarketTypeResults');
}

export function isMarketGroupResults(d: MarketGroupResults | CatalogMarketGroup): d is MarketGroupResults {
  return isTypename(d, 'MarketGroupResults');
}

export function isCatalogMarketGroup(d: MarketGroupResults | CatalogMarketGroup): d is CatalogMarketGroup {
  return isTypename(d, 'CatalogMarketGroup');
}
